import { useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import { FS, hoverOnly } from '../../../theme/tokens'
import { v2Tone, v2ToneHover } from '../../../theme/theme'

// 🔴 K-3: NUR die zwei Toene, deren MUI-Stock in `theme.js` NICHT gebunden ist.
// `primary`/`error`/`success` bleiben bei MUI (deren Slots sind eigene
// Entscheidungen, nicht dieser Befund).
const TON_ZU_V2 = { warning: 'warn', info: 'info' }

/**
 * InlineConfirmButton — single-pill expanding confirm (Pattern A v2).
 *
 * @description
 *   On click the pill expands leftward in place. No other element moves —
 *   the anchor reserves only the closed-button's width via an invisible
 *   sizer; the real pill is absolutely positioned and extends to the left
 *   over whatever sits there. Inside the expanded pill: the original label,
 *   a thin divider, the confirmation prompt, and a text-link "Cancel".
 *   Clicking the pill anywhere except the cancel link confirms.
 *
 *   Replaces the v1 design where the shell used inline-grid + a separate
 *   amber panel with nested buttons — that approach stretched the button
 *   when prompts wrapped and read as a "button inside a button".
 *
 * @useWhen
 *   - Irreversible but non-nuclear CTAs: send email, push to Shopify, create LSM DN
 *   - Action-bar / compose-footer contexts where a full dialog is overkill
 *   - Actions where the confirmation prompt should catch wrong-target mistakes
 *     (e.g. wrong recipient email)
 *
 * @dontUseFor
 *   - Destructive no-undo actions (delete, wipe) → use HoldButton (Pattern C)
 *   - Picker/dropdown with safe + side-effect options → use ConfirmOnSelectDropdown (Pattern B)
 *   - Safe idempotent actions → plain Button, no confirm
 *   - Actions needing multi-field input → full Dialog
 *
 * @related
 *   - HoldButton — hold-to-confirm for destructive actions
 *   - ConfirmOnSelectDropdown — consequence-panel on risky dropdown options
 *
 * @reuseHints
 *   - Any "push to external system" button (shipping label, invoice sync)
 *   - B2B credit approval, PO status transitions
 *
 * @props
 *   label         — trigger button label (stays visible when expanded)
 *   prompt        — confirmation question shown inside the expanded pill
 *   promptDetail  — optional second line under the prompt: the CONSEQUENCE
 *                   ("this changes live inventory"). Wraps instead of
 *                   truncating, so a consequence that does not fit the
 *                   220px prompt line is still fully readable at the moment
 *                   of the last refusal. Added 2026-10-04: the LSM panel's
 *                   dialog carried exactly such a sentence and lost it when
 *                   it moved to this pill.
 *   onConfirm     — async () => void; Promise in-flight shows spinner
 *   onCancel      — optional cancel callback
 *   cancelLabel   — default "Cancel" — shown as an underlined text link
 *   loadingLabel  — busy-state text (falls back to prompt)
 *   confirmLabel  — label of the rightmost part WHILE OPEN (falls back to label),
 *                   e.g. "Send" → "Send now". Closed, the trigger shows `label`.
 *   disabled, size, variant, color, startIcon, sx — MUI Button passthrough
 *                   🔴 `color="warning"|"info"` wird bei `outlined`/`text`
 *                   ueber die v2-Tonskala gebunden (K-3, UI2 Teil 8, 2026-10-10):
 *                   MUI liefert dort `palette.<ton>.main` als SCHRIFT auf dem
 *                   Papier — hell 3,113:1 (warning) / 3,857:1 (info), beide
 *                   unter 4,5:1. Bei `contained` bleibt es bei MUIs
 *                   Variantenpaarung; die anderen Toene bleiben unberuehrt.
 */
export default function InlineConfirmButton({
  label,
  prompt = 'Confirm?',
  promptDetail = null,
  onConfirm,
  onCancel,
  cancelLabel = 'Cancel',
  loadingLabel = null,
  // 🔴 Neun Aufrufer uebergaben `confirmLabel`, bevor es den Prop gab
  // (gemessen am 2026-10-04 in der Bausteinbibliothek). Er landete ueber
  // `...buttonProps` als unbekanntes Attribut am <button> — React warnte, und
  // der gewollte Text („Send now", „Create in LSM") erschien nie.
  confirmLabel = null,
  disabled = false,
  size = 'medium',
  variant = 'contained',
  color = 'warning',
  startIcon = null,
  sx = {},
  ...buttonProps
}) {
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(busy)
  useEffect(() => { busyRef.current = busy }, [busy])

  // Close on Esc or click outside (but not while busy — await the in-flight action)
  useEffect(() => {
    if (!open) return
    const safeClose = () => { if (!busyRef.current) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') safeClose() }
    const onClick = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) safeClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  const handleConfirm = async () => {
    if (busy) return
    try {
      setBusy(true)
      await Promise.resolve(onConfirm?.())
      setOpen(false)
    } finally {
      setBusy(false)
    }
  }

  const handleCancel = () => {
    if (busy) return
    setOpen(false)
    onCancel?.()
  }

  // Click on the real pill. If the event started on the cancel text-link
  // (data-cancel attribute on or above target), cancel. Otherwise: if closed,
  // open; if open, confirm.
  const handlePillClick = (e) => {
    if (e.target.closest('[data-inline-cancel]')) {
      e.stopPropagation()
      handleCancel()
      return
    }
    if (!open) setOpen(true)
    else handleConfirm()
  }

  return (
    <Box
      ref={anchorRef}
      sx={{
        position: 'relative',
        display: 'inline-block',
        verticalAlign: 'middle',
        ...sx,
      }}
    >
      {/* Invisible sizer — reserves closed-button space in the flex flow so
          sibling elements (Discard, Regenerate, etc.) never move when the
          real pill expands to the left. */}
      <Button
        variant={variant}
        color={color}
        size={size}
        startIcon={startIcon}
        disabled
        aria-hidden
        tabIndex={-1}
        sx={{ visibility: 'hidden', pointerEvents: 'none', whiteSpace: 'nowrap' }}
      >
        {label}
      </Button>

      {/* Real pill — absolute, anchored to the anchor's right edge. Grows to
          the LEFT when opened, overlaying whatever sits there. */}
      <Button
        variant={variant}
        color={color}
        size={size}
        disabled={disabled}
        startIcon={busy
          ? <CircularProgress size={14} color="inherit" />
          : startIcon}
        onClick={handlePillClick}
        // R16-A W1: der Name des OFFENEN Knopfes war die Verkettung seiner
        // Kinder ("Cancel Send to a@b? Send now"). Ein explizites aria-label
        // benennt den Zustand sauber. ZUGEKLAPPT kein aria-label — dann traegt
        // der Knopf seinen sichtbaren Text (die versteckten Teile sind per
        // `visibility: hidden` aus dem Baum, siehe unten).
        aria-label={open
          ? `${prompt} — ${busy ? (loadingLabel || label) : (confirmLabel || label)}`
          : undefined}
        sx={(t) => ({
          position: 'absolute',
          top: 0,
          right: 0,
          whiteSpace: 'nowrap',
          transition: 'padding 280ms cubic-bezier(.22,1,.36,1)',
          zIndex: 2,
          // K-3 (UI2 Teil 8, 2026-10-10): `color="warning"|"info"` loest ueber
          // `palette.<ton>.main` auf. Bei `outlined`/`text` ist der Ton die
          // SCHRIFT auf dem Papier — hell 3,113:1 (warning) / 3,857:1 (info),
          // beide unter 4,5:1 (WCAG 1.4.3). Der v2-Ton traegt in beiden Modi
          // (warnText #b45309 = 5,022:1 hell / 8,763:1 dunkel, infoText
          // #2563eb = 5,169:1 / 5,754:1). Bei `contained` bleibt es bei MUIs
          // Variantenpaarung (Schrift auf der Fuellung), die dieser Baustein
          // nicht anfasst.
          ...(TON_ZU_V2[color] && variant !== 'contained' && {
            color: v2Tone(t, TON_ZU_V2[color]).main,
            ...(variant === 'outlined' && {
              borderColor: v2Tone(t, TON_ZU_V2[color]).main,
              // R16-C2: ohne den `color`-Prop faellt MUIs `outlined`-Hover auf
              // `primary` zurueck (blauer Rand, blaue Toenung) — der Hover
              // gehoert in denselben Ton wie die Schrift.
              ...v2ToneHover(t, TON_ZU_V2[color]),
            }),
          }),
        })}
        {...buttonProps}
      >
        {/* Extra confirmation area — hidden when closed. Rendered BEFORE the
            label so that when open, Cancel + prompt sit to the LEFT and the
            Send label stays rightmost — exactly where the cursor is after the
            first click. User just clicks again without moving to confirm. */}
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: open ? 320 : 0,
            // 🔴 ZUGEKLAPPT MUSS DER BLOCK AUCH IN DER HOEHE VERSCHWINDEN.
            // `maxWidth: 0` klemmt nur die BREITE. Der Block bleibt im Fluss,
            // und sein Inhalt meldet weiter seine Inhaltshoehe: `promptDetail`
            // traegt `whiteSpace: normal` in einer Spalte, die auf Breite 0
            // gestreckt ist — die Folge bricht damit auf EIN WORT JE ZEILE um.
            // Gemessen am 2026-10-07: an einem statischen Nachbau derselben
            // Flex-Struktur in Chromium 267 px Hoehe zugeklappt (ohne
            // `promptDetail` 33 px, mit `height: 0` wieder 33 px), und in der
            // laufenden App an der Befundkarte 272 px — Rotbeweis in
            // `frontend/tests/rma/befunde-karte.spec.ts`. Die Pille steht
            // absolut auf `top: 0` in einem ~33 px hohen Anker; sie lief damit
            // rund 240 px aus der Karte heraus und ueber die naechste
            // (Befundkarte, Prod, 2026-10-07).
            // Ohne `promptDetail` ist die Wirkung null: der Block ist dann
            // einzeilig und ohnehin nicht hoeher als die Beschriftung.
            height: open ? 'auto' : 0,
            opacity: open ? 1 : 0,
            marginRight: open ? 1 : 0,
            // 🔴 ZUGEKLAPPT HEISST NICHT NUR UNSICHTBAR. `maxWidth: 0` und
            // `opacity: 0` lassen den Inhalt im ZUGAENGLICHKEITSBAUM stehen —
            // gemessen am 2026-10-03 trug der zugeklappte Ausloeser den Namen
            // "CancelRemove this file?Remove". Ein Screenreader-Nutzer hoerte
            // Abbrechen-Link, Rueckfrage und Beschriftung als einen Salat,
            // bevor er irgendetwas getan hatte.
            //
            // `visibility` und nicht `aria-hidden`: `visibility: hidden` nimmt
            // den zugeklappten Block aus dem Zuganglichkeitsbaum UND aus der
            // Tabreihenfolge und laesst die uebrigen Uebergaenge laufen. (Der
            // frueher hier sitzende Abbrechen-Link mit `tabIndex` ist seit
            // R16-B W8 reiner Text — der Grund von damals traegt nicht mehr.)
            visibility: open ? 'visible' : 'hidden',
            transition: open
              ? 'max-width 320ms cubic-bezier(.22,1,.36,1), opacity 200ms ease-out 120ms, margin-right 320ms'
              : 'max-width 280ms cubic-bezier(.22,1,.36,1), opacity 160ms ease-out, margin-right 280ms',
          }}
        >
          {/* Cancel (reiner Text, kein Link) — leftmost. 🔴 KEIN `role="button"` und kein
              `tabIndex` mehr (R16-B W8): ein fokussierbares Bedienelement INNERHALB
              eines `<button>` ist nach HTML/ARIA unzulaessig (interaktiver
              Nachfahre). Der MAUS-Klick wird ueber `data-inline-cancel` vom
              aeusseren Knopf geroutet (`handlePillClick`); der TASTATUR-Weg zum
              Abbrechen ist Escape (siehe den keydown-Effekt oben). Der aeussere
              Knopf traegt im offenen Zustand ein sauberes `aria-label`, deshalb
              zaehlt dieser Text nicht mehr in seinen Namen.
              ⚠️ Offen: ein erstrangiger Tastatur-Weg fuer „Cancel" braucht einen
              Umbau der Pill-Struktur (Cancel als Geschwister des <button>) —
              abgelegt unter docs/backlog-auto-repair/. */}
          <Box
            component="span"
            data-inline-cancel
            sx={{
              cursor: busy ? 'default' : 'pointer',
              fontWeight: 500,
              fontSize: 12,
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              padding: '0 2px',
              color: 'inherit',
              opacity: busy ? 0.4 : 1,
              // K-5 (Welle 2): reine Wirkung (Unterstreichung + Deckkraft) ohne
              // Hintergrund — auch die BEDINGTE Form klebt auf einem groben Zeiger.
              ...hoverOnly(busy ? {} : { textDecoration: 'none', opacity: 0.85 }),
            }}
          >
            {cancelLabel}
          </Box>
          {/* Prompt text — middle, truncates with ellipsis past ~220px.
              `promptDetail` sitzt darunter und BRICHT UM statt zu kuerzen: die
              Konsequenz einer Handlung darf nicht an der Ellipse sterben. */}
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              flexDirection: 'column',
              minWidth: 0,
              maxWidth: promptDetail ? 260 : 220,
              textAlign: 'left',
            }}
          >
            <Box
              component="span"
              sx={{
                fontWeight: 500,
                opacity: 0.7,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {busy ? (loadingLabel || prompt) : prompt}
            </Box>
            {promptDetail && (
              <Box
                component="span"
                sx={{
                  fontSize: FS.xs,
                  fontWeight: 400,
                  lineHeight: 1.25,
                  opacity: 0.65,
                  whiteSpace: 'normal',
                }}
              >
                {promptDetail}
              </Box>
            )}
          </Box>
          {/* Divider between prompt and the rightmost Send label */}
          <Box
            component="span"
            sx={{
              width: '1px',
              height: 14,
              bgcolor: 'rgba(0,0,0,0.22)',
              flexShrink: 0,
            }}
          />
        </Box>

        {/* Primary label — rightmost. Always visible. Cursor lands here after
            the first click; clicking again confirms without moving. */}
        <Box component="span">
          {busy ? (loadingLabel || label) : (open && confirmLabel) || label}
        </Box>
      </Button>
    </Box>
  )
}
